import { useEffect } from 'react';

interface DocumentMetaProps {
  title: string;
  description?: string;
  keywords?: string;
  author?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: Record<string, any>;
}

/**
 * 🚀 React 19 Enhanced Document Metadata Hook
 * 
 * Enhanced hook pentru management dinamic și comprehensiv de document metadata.
 * Optimizat pentru React 19 cu suport complet pentru SEO și social media.
 * 
 * @param title - Titlul paginii
 * @param description - Descrierea pentru meta tag (optional)
 * @param keywords - Keywords pentru SEO (optional)
 * @param author - Autorul conținutului (optional)
 * @param ogTitle - Open Graph title (optional, default: title)
 * @param ogDescription - Open Graph description (optional, default: description)
 * @param ogImage - Open Graph image URL (optional)
 * @param ogType - Open Graph type (optional, default: 'website')
 * @param twitterCard - Twitter Card type (optional, default: 'summary')
 * @param twitterTitle - Twitter title (optional, default: title)
 * @param twitterDescription - Twitter description (optional, default: description)
 * @param twitterImage - Twitter image URL (optional, default: ogImage)
 * @param structuredData - JSON-LD structured data (optional)
 * 
 * @example
 * ```tsx
 * useDocumentMeta({
 *   title: 'Tranzacții - Buzunarul',
 *   description: 'Gestionează tranzacțiile financiare eficient',
 *   keywords: 'tranzacții, finanțe, buget, România',
 *   ogTitle: 'Gestionare Tranzacții - Buzunarul',
 *   ogDescription: 'Aplicația ta financiară personală',
 *   ogImage: '/images/transactions-preview.jpg',
 *   structuredData: {
 *     "@context": "https://schema.org",
 *     "@type": "WebApplication",
 *     "name": "Buzunarul"
 *   }
 * });
 * ```
 */
export function useDocumentMeta({ 
  title, 
  description, 
  keywords,
  author = 'Buzunarul',
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary',
  twitterTitle,
  twitterDescription,
  twitterImage,
  structuredData
}: DocumentMetaProps) {
  useEffect(() => {
    // 🎯 React 19 Native Document Title Update
    document.title = title;
    
    // Helper function pentru meta tag management
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let metaTag = document.querySelector(selector);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(property ? 'property' : 'name', name);
        document.head.appendChild(metaTag);
      }
      
      metaTag.setAttribute('content', content);
    };

    // 🔍 Basic SEO Meta Tags
    if (description) {
      updateMetaTag('description', description);
    }
    
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    
    if (author) {
      updateMetaTag('author', author);
    }

    // 📱 Viewport și mobile optimization
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0');
    updateMetaTag('robots', 'index, follow');
    
    // 🌐 Open Graph Meta Tags pentru social media
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:site_name', 'Buzunarul', true);
    updateMetaTag('og:locale', 'ro_RO', true);
    
    if (ogDescription || description) {
      updateMetaTag('og:description', ogDescription || description!, true);
    }
    
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
      updateMetaTag('og:image:alt', ogTitle || title, true);
    }

    // 🐦 Twitter Card Meta Tags
    updateMetaTag('twitter:card', twitterCard);
    updateMetaTag('twitter:title', twitterTitle || ogTitle || title);
    updateMetaTag('twitter:site', '@buzunarul');
    
    if (twitterDescription || ogDescription || description) {
      updateMetaTag('twitter:description', twitterDescription || ogDescription || description!);
    }
    
    if (twitterImage || ogImage) {
      updateMetaTag('twitter:image', twitterImage || ogImage!);
    }

    // 🏗️ JSON-LD Structured Data
    if (structuredData) {
      // Remove existing structured data
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // 🎨 Theme color și PWA meta tags
    updateMetaTag('theme-color', '#3b82f6');
    updateMetaTag('msapplication-TileColor', '#3b82f6');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    
    // 🔐 Security headers
    updateMetaTag('referrer', 'strict-origin-when-cross-origin');
    
  }, [title, description, keywords, author, ogTitle, ogDescription, ogImage, ogType, twitterCard, twitterTitle, twitterDescription, twitterImage, structuredData]);
}

export default useDocumentMeta; 